import Dialog from "@rio-cloud/rio-uikit/Dialog";
import ReceiptForm, { FormState, NameInputChange } from "./form";
import { useFormReceipt } from "./useFormReceipt";
import React, { useEffect, useState } from "react";
import DialogCreateType from "../Create";
import DialogCreateOperation from "../CreateOperation";
import { createBranch, createCategory, createSegments, createSubCategories, createSuppliers } from "@services";
import { Notification } from "@rio-cloud/rio-uikit";
import { ICreateOperationPayload } from "@types";

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (data: FormState, callBack?: () => void, closeModal?: boolean) => void;
  title: string;
  selected?: FormState;
  handleFile?: (file: File) => void;
  handleClearFile: () => void;
  fileName?: string;
  changeFileName: (fileName: string) => void;
}

const defaultOption = {
  open: false,
  type: "",
  title: "",
  value: "",
  placeholder: "",
};

interface Option {
  [x: string]: { placeholder: string; title: string };
}

const DialogReceipt = ({
  show,
  fileName,
  onClose,
  onSave,
  title,
  selected,
  handleFile,
  handleClearFile,
  changeFileName,
}: Props) => {
  const {
    branchs,
    segments,
    supplier,
    drivers,
    categories,
    subCategories,
    vehicles,
    form,
    setForm,
    fetchBranchs,
    fetchSegments,
    fetchSupplier,
    fetchCategories,
    fetchSubCategories,
    initialDefaultForm,
  } = useFormReceipt();

  useEffect(() => {
    if (selected) {
      setForm(selected);
    }
  }, [selected]);

  const [dialogOption, setDialogOption] = useState(defaultOption);
  const [showOperationDialog, setShowOperationDialog] = useState(false);

  const onCreateNewOption = (name: NameInputChange) => {
    // Operacao/Segmento usa dialog especifico com 2 campos
    // if (name === 'segment') {
    //     setShowOperationDialog(true);
    //     return;
    // }

    const opt: Option = {
      branch: {
        placeholder: "Qual o nome da nova filial?",
        title: "Nova filial",
      },
      supplier: {
        placeholder: "Qual o nome do novo(a) cliente?",
        title: "Novo(a) cliente",
      },
      category: {
        placeholder: "Qual a nova categoria?",
        title: "Nova categoria",
      },
      subcategory: {
        placeholder: "Qual a nova subcategoria?",
        title: "Nova subcategoria",
      },
      segment: {
        placeholder: "Qual o novo segmento?",
        title: "Novo segmento",
      },
    };

    setDialogOption({
      open: true,
      placeholder: opt[name]?.placeholder,
      title: opt[name]?.title,
      type: name,
      value: "",
    });
  };

  const handleSaveBranch = async (value: string) => {
    try {
      const response = await createBranch({
        name: value,
      });
      fetchBranchs();
      setForm({
        ...form,
        branch: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Filial criada com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar a filial no momento.");
    }
  };

  const handleSaveOperation = async (value: string) => {
    try {
      const response = await createSegments(value);
      fetchSegments();
      setForm({
        ...form,
        segment: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Segmento criado com sucesso.");
    } catch (error) {
      Notification.error("Nao foi possivel criar a operacao no momento.");
    }
  };

  const handleSaveAccounts = async (value: string) => {
    try {
      const response = await createSuppliers({
        name: value,
        type: "CLIENTE",
      });
      fetchSupplier();
      setForm({
        ...form,
        supplier: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Conta criada com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar a conta no momento.");
    }
  };

  const handleSaveCategory = async (value: string) => {
    try {
      const response = await createCategory({
        name: value,
        type: "RECEITA",
      });
      fetchCategories();
      setForm({
        ...form,
        category: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Categoria criada com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar a categoria no momento.");
    }
  };

  const handleSaveSubCategory = async (value: string) => {
    try {
      const response = await createSubCategories(form.category, { name: value });
      fetchSubCategories();
      setForm({
        ...form,
        subcategory: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Subcategoria criada com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar a subcategoria no momento.");
    }
  };

  const handleSave = (value: string) => {
    if (dialogOption.type === "branch") {
      handleSaveBranch(value);
      return;
    }

    if (dialogOption.type === "supplier") {
      handleSaveAccounts(value);
      return;
    }

    if (dialogOption.type === "category") {
      handleSaveCategory(value);
      return;
    }

    if (dialogOption.type === "subcategory") {
      handleSaveSubCategory(value);
      return;
    }

    if (dialogOption.type === "segment") {
      handleSaveOperation(value);
      return;
    }
  };

  const handleDefaultForm = () => {
    setForm(initialDefaultForm);
  };

  const handleDefault = () => {
    onClose();
    handleDefaultForm();
  };

  return (
    <React.Fragment>
      <Dialog
        show={show && !dialogOption.open}
        title={<span className="text-medium text-size-16">{title}</span>}
        body={
          <ReceiptForm
            form={form}
            vehicles={vehicles}
            setForm={setForm}
            branchs={branchs}
            segments={segments}
            suppliers={supplier}
            drivers={drivers}
            categories={categories}
            subCategories={subCategories}
            openFormModal={onClose}
            openNewOptionModal={() => {}}
            onSave={(form) => onSave(form, handleDefaultForm)}
            onSaveAndCreateNew={(form) => onSave(form, handleDefaultForm, false)}
            onCreateNewOption={onCreateNewOption}
            onCancel={handleDefault}
            isEdit={!!selected}
            handleFile={handleFile}
            handleClearFile={handleClearFile}
            fileName={fileName}
            changeFileName={changeFileName}
          />
        }
        onClose={handleDefault}
        showCloseButton
        useOverflow
      />
      <DialogCreateType
        title={dialogOption.title}
        show={dialogOption.open}
        placeholder={dialogOption.placeholder}
        onClose={() => setDialogOption(defaultOption)}
        onSave={handleSave}
      />

      {/* <DialogCreateOperation
                show={showOperationDialog}
                onClose={() => setShowOperationDialog(false)}
                onSave={handleSaveOperation}
            /> */}
    </React.Fragment>
  );
};

export default DialogReceipt;
